/** Role model for the plan of record. Capabilities are IdP-mapped. See docs/PRD_MOCKUPS_DESIGN_NOTES.md. */

export type PlanCapability =
  | "plan.view"
  | "plan.edit"
  /**
   * Changing which roles may see a study document. Kept separate from `plan.edit` because
   * it is a governance action rather than a data correction: widening access can expose
   * information, and nothing can un-expose it.
   */
  | "documents.manage-access";

export interface PlanRole {
  id: string;
  name: string;
  description: string;
  capabilities: PlanCapability[];
}

export const PLAN_ROLES: PlanRole[] = [
  {
    id: "plan-viewer",
    name: "Plan viewer",
    description:
      "Can read the plan of record and its full change history. Cannot change any value. This is what general trial access grants.",
    capabilities: ["plan.view"],
  },
  {
    id: "plan-editor",
    name: "Plan editor",
    description:
      "Can enter and correct individual plan values — milestone dates, statuses, enrolment targets. Every change requires a rationale and is attributed to them in the change history.",
    capabilities: ["plan.view", "plan.edit", "documents.manage-access"],
  },
];

export interface PlanUser {
  id: string;
  name: string;
  jobTitle: string;
  roleId: string;
  /** The IdP group the capability came from — Vivo maps it, it does not own it. */
  grantedVia: string;
  grantedAt: string;
}

export const PLAN_USERS: PlanUser[] = [
  {
    id: "u-dana",
    name: "Dana Whitfield",
    jobTitle: "ClinOps Study Lead",
    roleId: "plan-editor",
    grantedVia: "AzureAD · Vivo-Plan-Editors",
    grantedAt: "2026-02-09T10:00:00Z",
  },
  {
    id: "u-marcus",
    name: "Marcus Bell",
    jobTitle: "Clinical Trial Manager",
    roleId: "plan-editor",
    grantedVia: "AzureAD · Vivo-Plan-Editors",
    grantedAt: "2026-03-04T09:30:00Z",
  },
  {
    id: "u-priya",
    name: "Priya Raman",
    jobTitle: "Clinical Research Associate",
    roleId: "plan-editor",
    grantedVia: "AzureAD · Vivo-Plan-Editors",
    grantedAt: "2026-04-22T14:10:00Z",
  },
  {
    id: "u-alan",
    name: "Dr. Alan Reyes",
    jobTitle: "Medical Monitor",
    roleId: "plan-viewer",
    grantedVia: "AzureAD · Vivo-Trial-Users",
    grantedAt: "2026-02-09T10:00:00Z",
  },
];

/**
 * Both roles carry plan-write capability, and the server agrees: its write gate
 * is `PLAN_EDIT_ROLES = {plan_editor, full_access}` (views_plan_of_record.py),
 * so `full_access` alone is enough to edit — no second `plan_editor` grant.
 *
 * Keep `full_access` in this check. Beyond matching the server, the user-info
 * endpoint surfaces only one role string per trial (trial_roles[0]), and
 * `full_access` sorts ahead of `plan_editor` there — so a user holding both
 * reads as `full_access` and would lose the write affordance without it.
 */
export function hasPlanEditorRole(grantedRole: string | undefined): boolean {
  return grantedRole === "plan_editor" || grantedRole === "full_access";
}

export function roleFor(user: PlanUser): PlanRole {
  const role = PLAN_ROLES.find((candidate) => candidate.id === user.roleId);
  if (!role) throw new Error(`Unknown plan role: ${user.roleId}`);
  return role;
}

export function can(user: PlanUser, capability: PlanCapability): boolean {
  return roleFor(user).capabilities.includes(capability);
}
